import { ReactElement, useCallback, useMemo } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { Menu, MenuItem } from '@mui/material';
import { useIntl } from 'react-intl';
import { OpenEditDirectoryEvent } from 'presentation/directory/openDirectoryInput';
import {
  DocumentationViewItem,
  isDirectoryItemType,
} from 'store/documentation/model/types';
import {
  isArchived,
} from '../model';
import { GridFilterModel } from '@mui/x-data-grid-pro';
import {
  ContextMenuPosition,
  useContextMenu,
} from 'components/common/dataGrid/hooks/useContextMenu';
import { confirmDocumentationOperation } from 'components/documentation/documentationOperationDialog';
import { useDocumentationStore } from 'store/documentation';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useAsyncDialog } from 'components/core/Dialog/common/useAsyncDialog';
import { documentationDownloadDirectoryDialog } from 'components/documentation/documentationDownloadDialog';
import {
  DocumentationOperationType,
} from 'shared/domain/documentation/operationPreflight';
import { useValueGetters } from '../hooks/useValueGetters';
import { useProjectTimezone } from 'store/project';

export { useContextMenu };

interface DocumentationContextMenuProps {
  contextMenu: ContextMenuPosition | null;
  handleClose: () => void;
  editFile: () => void;
  editDirectory: (editDirectoryEvent: OpenEditDirectoryEvent) => void;
  filterModel: GridFilterModel;
  onMove: () => void;
}

export function DocumentationContextMenu({
  contextMenu,
  handleClose,
  editFile,
  editDirectory,
  filterModel,
  onMove,
}: DocumentationContextMenuProps): ReactElement {
  const intl = useIntl();
  const createDialog = useDialog();
  const createAsyncDialog = useAsyncDialog();
  const timezone = useProjectTimezone();

  const selectedItemsSet = useDocumentationStore.use.selectedItems();
  const prepareSelectedOperation =
    useDocumentationStore.use.prepareSelectedOperation();
  const confirmPreparedOperation =
    useDocumentationStore.use.confirmPreparedOperation();
  const clearPreparedOperation =
    useDocumentationStore.use.clearPreparedOperation();
  const downloadFile = useDocumentationStore.use.downloadFile();
  const downloadDirectory = useDocumentationStore.use.downloadDirectory();
  const documentState = useDocumentationStore.use.documentState();
  const valueGetters = useValueGetters();

  const selectedItems = useMemo(
    () => Array.from(selectedItemsSet),
    [selectedItemsSet]
  );
  const selectedItem = selectedItems.length === 1 ? selectedItems[0] : null;

  const performOperation = useCallback(
    async (operationType: DocumentationOperationType): Promise<void> => {
      const prepared = await prepareSelectedOperation(operationType);
      if (!prepared || prepared.resolvedCount === 0) {
        return;
      }

      await confirmDocumentationOperation({
        prepared,
        createAsyncDialog,
        confirmPreparedOperation,
        getPreparedOperation: () => useDocumentationStore.getState().preparedOperation,
        clearPreparedOperation,
      });
    },
    [
      clearPreparedOperation,
      confirmPreparedOperation,
      createAsyncDialog,
      prepareSelectedOperation,
    ]
  );

  const downloadSingleFile = useCallback(
    async (item: DocumentationViewItem) => {
      try {
        await downloadFile(item, {
          filterModel,
          timezone,
          valueGetters,
          documentState,
        });
      } catch {
        // Error handling will be added in dedicated follow-up.
      }
    },
    [downloadFile, filterModel, timezone, valueGetters, documentState]
  );

  const downloadSingleDirectory = useCallback(
    async (item: DocumentationViewItem) => {
      try {
        await createDialog(documentationDownloadDirectoryDialog());
        await downloadDirectory(item, {
          filterModel,
          timezone,
          valueGetters,
          documentState,
        });
      } catch {
        // Error handling will be added in dedicated follow-up.
      }
    },
    [
      createDialog,
      downloadDirectory,
      filterModel,
      timezone,
      valueGetters,
      documentState,
    ]
  );

  const handleEdit = useCallback(() => {
    if (!selectedItem) {
      return;
    }

    handleClose();

    if (isDirectoryItemType(selectedItem.type)) {
      editDirectory(selectedItem);
      return;
    }

    editFile();
  }, [selectedItem, handleClose, editDirectory, filterModel, editFile]);

  const handleArchive = useCallback(() => {
    if (!selectedItem || isArchived(selectedItem)) {
      return;
    }

    handleClose();
    performOperation(DocumentationOperationType.archive);
  }, [handleClose, performOperation, selectedItem]);

  const handleRestore = useCallback(() => {
    if (!selectedItem || !isArchived(selectedItem)) {
      return;
    }

    handleClose();
    performOperation(DocumentationOperationType.restore);
  }, [handleClose, performOperation, selectedItem]);

  const handleDownload = useCallback(() => {
    if (!selectedItem) {
      return;
    }

    handleClose();

    if (isDirectoryItemType(selectedItem.type)) {
      downloadSingleDirectory(selectedItem);
      return;
    }

    downloadSingleFile(selectedItem);
  }, [
    selectedItem,
    handleClose,
    downloadSingleDirectory,
    downloadSingleFile,
  ]);

  const handleMove = useCallback(() => {
    if (!selectedItem) {
      return;
    }

    handleClose();
    onMove();
  }, [selectedItem, handleClose, onMove]);

  const selectedCount = selectedItem ? 1 : 0;
  const shouldShowRestore = Boolean(selectedItem && isArchived(selectedItem));

  return (
    <Menu
      open={contextMenu !== null}
      onClose={handleClose}
      anchorReference='anchorPosition'
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      {shouldShowRestore ? (
        <MenuItem onClick={handleRestore}>
          {formatTranslation(intl, CommonTranslationKeys.restore)}
        </MenuItem>
      ) : (
        <MenuItem onClick={handleArchive} disabled={!selectedItem}>
          {formatTranslation(intl, CommonTranslationKeys.archive)}
        </MenuItem>
      )}
      <MenuItem onClick={handleEdit} disabled={selectedCount !== 1}>
        {formatTranslation(intl, CommonTranslationKeys.edit)}
      </MenuItem>
      <MenuItem onClick={handleMove} disabled={!selectedItem}>
        {formatTranslation(intl, CommonTranslationKeys.move)}
      </MenuItem>
      <MenuItem onClick={handleDownload}>
        {formatTranslation(intl, CommonTranslationKeys.download)}
      </MenuItem>
    </Menu>
  );
}
